import { type MeetDetail, slotBib } from "./meets";

/**
 * A meet's start list for a venue console — what OVR hands the field console
 * (or any device that keys athletes by bib) BEFORE the event, so the console's
 * athletes are this start list's and its card can be applied back
 * (`timing-field.ts`, matched by bib).
 *
 *   OVR start list ──consoleStartList()──▶ JSON / CSV ──the operator loads it──▶ console
 *   console card { meetId, athletes[].bib } ──relay──▶ OVR "Apply field results"
 *
 * The console sends `meet_id` back as its card's `meetId`: a card for another
 * meet is then refused whole (`RACE_MISMATCH`). The bib is `slotBib` (this
 * meet's, else the athlete's for the event) — the very key Apply matches on —
 * so an athlete without one is listed with
 * `bib: null` and the console cannot score them until the start list has it.
 * Pure: built from the meet OVR already holds, no request of its own.
 */

export const CONSOLE_START_LIST_SCHEMA = "seagames.console-start-list/1";

export interface ConsoleStartListAthlete {
	/** The start list's order (1 first); athletes without one follow, by name. */
	order: number;
	/** `slotBib`: this meet's bib, else the athlete's; null = none yet (the console cannot key this athlete). */
	bib: string | null;
	/** The printed name, as OVR shows it ("SULAIMAN Aqil"). */
	name: string;
	/** The NOC code ("BRU"); null for an organisation with none. */
	noc: string | null;
	/** The country's common name ("Brunei"). */
	country: string | null;
}

export interface ConsoleStartList {
	schema: typeof CONSOLE_START_LIST_SCHEMA;
	/** Send it back as the card's `meetId`. */
	meet_id: string;
	meet_no: string;
	event_code: string;
	event_name: string;
	/** The console competition (`ATHLETICS_FIELD_DISCIPLINE_IDS`: "long", "high"…); null when the console knows none. */
	discipline: string | null;
	/** The round ("Final", "Qualification"). */
	phase: string;
	athletes: ConsoleStartListAthlete[];
}

type StartListMeet = Pick<MeetDetail, "meet_id" | "meet_no" | "competitors"> & {
	event: Pick<MeetDetail["event"], "code" | "name">;
	phase: Pick<MeetDetail["phase"], "name">;
};

/** The meet's filled slots, in start order, as a console start list. */
export function consoleStartList(meet: StartListMeet, discipline: string | null): ConsoleStartList {
	const filled = meet.competitors.filter((s) => s.competitor !== null);
	const sorted = [...filled].sort(
		(a, b) => (a.start_order ?? Number.POSITIVE_INFINITY) - (b.start_order ?? Number.POSITIVE_INFINITY) || (a.competitor?.name ?? "").localeCompare(b.competitor?.name ?? ""),
	);
	return {
		schema: CONSOLE_START_LIST_SCHEMA,
		meet_id: meet.meet_id,
		meet_no: meet.meet_no,
		event_code: meet.event.code,
		event_name: meet.event.name,
		discipline,
		phase: meet.phase.name,
		athletes: sorted.map((s, i) => {
			return {
				order: s.start_order ?? i + 1,
				bib: slotBib(s),
				name: s.competitor?.name ?? "",
				noc: s.competitor?.noc.code ?? null,
				country: s.competitor?.noc.country_name ?? null,
			};
		}),
	};
}

/** The athletes the console cannot key: no bib on the start list. */
export const consoleStartListMissingBibs = (list: ConsoleStartList): ConsoleStartListAthlete[] => list.athletes.filter((a) => a.bib === null);

const CSV_COLUMNS = ["order", "bib", "name", "noc", "country"] as const;

/** A CSV cell (RFC 4180): quoted when it holds a comma, a quote or a line break. */
const csvCell = (v: string | number | null): string => {
	const s = v === null ? "" : String(v);
	return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * The same list as CSV, for a console that loads a spreadsheet: a header row
 * (`order,bib,name,noc,country`), one row per athlete, CRLF line ends. The
 * meet travels in the file name and the JSON, not the rows.
 */
export function consoleStartListCsv(list: ConsoleStartList): string {
	const rows = [CSV_COLUMNS.join(","), ...list.athletes.map((a) => CSV_COLUMNS.map((c) => csvCell(a[c])).join(","))];
	return `${rows.join("\r\n")}\r\n`;
}

/** A file name for the downloads: `start-list-<meet no>-<event code>`, safe on any system. */
export const consoleStartListFileName = (list: ConsoleStartList): string =>
	`start-list-${list.meet_no}-${list.event_code}`.replace(/[^A-Za-z0-9._-]+/g, "_");
